import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/api'

// ─── PUBLIC ───────────────────────────────────────
export const get_blogs = createAsyncThunk(
    'blog/get_blogs',
    async ({ page = 1, limit = 9, category } = {}, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(
                `/blogs?page=${page}&limit=${limit}${category && category !== 'Hamısı' ? `&category=${category}` : ''}`
            )
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const get_blog = createAsyncThunk(
    'blog/get_blog',
    async (slug, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/blogs/${slug}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const get_latest_blogs = createAsyncThunk(
    'blog/get_latest_blogs',
    async (_, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get('/blogs/latest')
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

// ─── ADMIN ────────────────────────────────────────
export const add_blog = createAsyncThunk(
    'blog/add_blog',
    async (blogData, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post('/admin/blogs', blogData, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const update_blog = createAsyncThunk(
    'blog/update_blog',
    async ({ blogId, ...blogData }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.put(`/admin/blogs/${blogId}`, blogData, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const delete_blog = createAsyncThunk(
    'blog/delete_blog',
    async (blogId, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.delete(`/admin/blogs/${blogId}`, { withCredentials: true })
            return fulfillWithValue({ ...data, blogId })
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const get_admin_blogs = createAsyncThunk(
    'blog/get_admin_blogs',
    async ({ page = 1, limit = 10, search = '', status } = {}, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(
                `/admin/blogs?page=${page}&limit=${limit}&search=${search}${status ? `&status=${status}` : ''}`,
                { withCredentials: true }
            )
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const get_admin_blog = createAsyncThunk(
    'blog/get_admin_blog',
    async (blogId, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/admin/blogs/${blogId}`, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

// ─── SLICE ────────────────────────────────────────
export const blogReducer = createSlice({
    name: 'blog',
    initialState: {
        loader:         false,
        blogs:          [],
        blog:           null,
        relatedBlogs:   [],
        latestBlogs:    [],
        adminBlogs:     [],
        currentBlog:    null,
        total:          0,
        page:           1,
        pages:          1,
        successMessage: '',
        errorMessage:   ''
    },
    reducers: {
        messageClear: (state) => {
            state.successMessage = ''
            state.errorMessage   = ''
        }
    },
    extraReducers: (builder) => {
        builder
            // Get blogs (public)
            .addCase(get_blogs.pending, (state) => { state.loader = true })
            .addCase(get_blogs.fulfilled, (state, { payload }) => {
                state.loader = false
                state.blogs  = payload.blogs
                state.total  = payload.total
                state.page   = payload.page
                state.pages  = payload.pages
            })
            .addCase(get_blogs.rejected, (state) => { state.loader = false })

            // Get single blog
            .addCase(get_blog.fulfilled, (state, { payload }) => {
                state.blog         = payload.blog
                state.relatedBlogs = payload.related
            })

            // Latest blogs (homepage)
            .addCase(get_latest_blogs.fulfilled, (state, { payload }) => {
                state.latestBlogs = payload.blogs
            })

            // Admin blogs
            .addCase(get_admin_blogs.pending, (state) => { state.loader = true })
            .addCase(get_admin_blogs.fulfilled, (state, { payload }) => {
                state.loader     = false
                state.adminBlogs = payload.blogs
                state.total      = payload.total
            })
            .addCase(get_admin_blogs.rejected, (state) => { state.loader = false })

            // Get admin single blog
            .addCase(get_admin_blog.fulfilled, (state, { payload }) => {
                state.currentBlog = payload.blog
            })

            // Add blog
            .addCase(add_blog.pending, (state) => { state.loader = true })
            .addCase(add_blog.fulfilled, (state, { payload }) => {
                state.loader = false
                state.successMessage = payload.message
                state.adminBlogs.unshift(payload.blog)
            })
            .addCase(add_blog.rejected, (state, { payload }) => {
                state.loader = false
                state.errorMessage = payload?.message || 'Xəta baş verdi'
            })

            // Update blog
            .addCase(update_blog.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
                const idx = state.adminBlogs.findIndex(b => b._id === payload.blog._id)
                if (idx !== -1) state.adminBlogs[idx] = payload.blog
            })
            .addCase(update_blog.rejected, (state, { payload }) => {
                state.errorMessage = payload?.message || 'Yenilənmədi'
            })

            // Delete blog
            .addCase(delete_blog.fulfilled, (state, { payload }) => {
                state.successMessage = payload.message
                state.adminBlogs = state.adminBlogs.filter(b => b._id !== payload.blogId)
            })
    }
})

export const { messageClear } = blogReducer.actions
export default blogReducer.reducer